// src/lib/requirements-matcher.ts
// Matches RFP requirements to evidence from the profile inventory

import {
  ContractScoredLibrary,
  ScoredEvidenceItem,
  RFPRequirement,
  RequirementMatch,
  MatchingResult,
  MatchStatus,
  MatchType,
  EvidenceMatch,
  RequirementGap,
  GapRecommendation
} from '../types/source-grounded';

import {
  findCanonicalCapability,
  searchCapabilities,
  areCapabilitiesEquivalent
} from './taxonomy';

/**
 * Configuration for the requirements matcher
 */
export interface MatcherConfig {
  minRelevanceForMatch: number;        // Minimum relevance score to consider (default 0.3)
  fullMatchThreshold: number;          // Score threshold for FULL match (default 0.7)
  partialMatchThreshold: number;       // Score threshold for PARTIAL match (default 0.4)
  maxMatchesPerRequirement: number;    // Max evidence items per requirement (default 5)
  preferDirectOverInferred: boolean;   // Prefer direct matches over inferred (default true)
}

const DEFAULT_CONFIG: MatcherConfig = {
  minRelevanceForMatch: 0.3,
  fullMatchThreshold: 0.7,
  partialMatchThreshold: 0.4,
  maxMatchesPerRequirement: 5,
  preferDirectOverInferred: true
};

/**
 * Requirements Matcher
 * Matches each RFP requirement to evidence from the scored library
 */
export class RequirementsMatcher {
  private config: MatcherConfig;

  constructor(config: Partial<MatcherConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Match all requirements against the evidence library
   */
  matchRequirements(
    requirements: RFPRequirement[],
    library: ContractScoredLibrary
  ): MatchingResult {
    // Ensure requirements is an array
    const safeRequirements = Array.isArray(requirements) ? requirements : [];
    console.log(`🔍 Matching ${safeRequirements.length} requirements against ${library.totalEvidenceCount || 0} evidence items`);

    const matches: RequirementMatch[] = [];
    let fullMatches = 0;
    let partialMatches = 0;
    let noMatches = 0;
    const allRecommendations: GapRecommendation[] = [];

    for (const requirement of safeRequirements) {
      const match = this.matchSingleRequirement(requirement, library);
      matches.push(match);

      if (match.matchStatus === 'FULL') fullMatches++;
      else if (match.matchStatus === 'PARTIAL') partialMatches++;
      else noMatches++;

      allRecommendations.push(...match.recommendations);
    }

    // Calculate overall readiness
    const overallReadiness = safeRequirements.length > 0
      ? Math.round((fullMatches / safeRequirements.length) * 100)
      : 0;

    // Count critical gaps
    const criticalGapsCount = matches.reduce(
      (count, m) => count + m.gaps.filter(g => g.severity === 'CRITICAL').length,
      0
    );

    // Deduplicate and prioritize recommendations
    const uniqueRecommendations = this.deduplicateRecommendations(allRecommendations);

    const result: MatchingResult = {
      contractId: library.contractId,
      contractTitle: library.contractTitle,
      totalRequirements: safeRequirements.length,
      fullMatches,
      partialMatches,
      noMatches,
      overallReadiness,
      matches,
      criticalGapsCount,
      recommendations: uniqueRecommendations
    };

    console.log(`✅ Matching complete: ${fullMatches} FULL, ${partialMatches} PARTIAL, ${noMatches} NONE`);
    console.log(`   Overall readiness: ${overallReadiness}%`);
    console.log(`   Critical gaps: ${criticalGapsCount}`);

    return result;
  }

  /**
   * Match a single requirement against the evidence library
   */
  matchSingleRequirement(
    requirement: RFPRequirement,
    library: ContractScoredLibrary
  ): RequirementMatch {
    // Extract keywords and capabilities from requirement
    const requirementKeywords = this.extractMatchKeywords(requirement);
    const requiredCapabilities = this.extractRequiredCapabilities(requirement);

    // Find matching evidence
    const candidates: Array<{
      evidence: ScoredEvidenceItem;
      matchScore: number;
      matchType: MatchType;
      matchedOn: string[];
    }> = [];

    for (const evidence of (library?.scoredEvidence || [])) {
      // Skip if relevance is too low
      if (evidence.relevance.overallScore < this.config.minRelevanceForMatch) {
        continue;
      }

      const matchResult = this.scoreEvidenceAgainstRequirement(
        evidence,
        requirementKeywords,
        requiredCapabilities
      );

      if (matchResult.score > 0) {
        candidates.push({
          evidence,
          matchScore: matchResult.score,
          matchType: matchResult.matchType,
          matchedOn: matchResult.matchedOn
        });
      }
    }

    // Sort candidates by score (and prefer direct matches)
    candidates.sort((a, b) => {
      if (this.config.preferDirectOverInferred) {
        if (a.matchType === 'DIRECT' && b.matchType === 'INFERRED') return -1;
        if (a.matchType === 'INFERRED' && b.matchType === 'DIRECT') return 1;
      }
      return b.matchScore - a.matchScore;
    });

    // Take top matches
    const topCandidates = candidates.slice(0, this.config.maxMatchesPerRequirement);

    // Convert to EvidenceMatch format
    const matchedEvidence: EvidenceMatch[] = topCandidates.map(c => ({
      evidenceId: c.evidence.evidenceId,
      source: `${c.evidence.sourceType}: ${c.evidence.contentSummary}`,
      content: this.extractRelevantContent(c.evidence, c.matchedOn),
      relevanceScore: c.matchScore,
      matchType: c.matchType,
      inferenceChain: c.matchType === 'INFERRED'
        ? this.getInferenceChain(c.evidence, c.matchedOn)
        : undefined,
      citationFormat: this.formatCitation(c.evidence, c.matchType)
    }));

    // Determine match status
    const matchStatus = this.determineMatchStatus(matchedEvidence);

    // Identify gaps
    const gaps = this.identifyGaps(requirement, matchedEvidence, requiredCapabilities);

    // Generate recommendations
    const recommendations = this.generateRecommendations(requirement, gaps, requiredCapabilities);

    // Calculate confidence score
    const confidenceScore = this.calculateConfidence(matchedEvidence);

    return {
      requirementId: requirement.id,
      requirementText: requirement.text,
      requirementSource: requirement.source,
      requirementCategory: requirement.category,
      matchStatus,
      matchedEvidence,
      gaps,
      recommendations,
      confidenceScore
    };
  }

  /**
   * Extract keywords for matching from requirement
   */
  private extractMatchKeywords(requirement: RFPRequirement): string[] {
    const keywords: string[] = [...requirement.keywords];

    // Also extract from text
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'shall',
      'must', 'will', 'should', 'may', 'can', 'be', 'been', 'being',
      'contractor', 'government', 'agency', 'provide', 'services'
    ]);

    const textWords = requirement.text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w));

    keywords.push(...textWords);

    return [...new Set(keywords)];
  }

  /**
   * Extract required capabilities from requirement text
   */
  private extractRequiredCapabilities(requirement: RFPRequirement): string[] {
    const capabilities: string[] = [];
    const text = requirement.text.toLowerCase();

    // Check for capability synonyms
    const capabilityResults = searchCapabilities(text);
    for (const cap of capabilityResults) {
      capabilities.push(cap.canonicalId);
    }

    // Check common patterns
    const patterns = [
      /experience\s+(?:in|with)\s+([a-z\s]+?)(?:\.|,|and|or|$)/gi,
      /expertise\s+in\s+([a-z\s]+?)(?:\.|,|and|or|$)/gi,
      /capability\s+(?:in|to)\s+([a-z\s]+?)(?:\.|,|and|or|$)/gi,
      /knowledge\s+of\s+([a-z\s]+?)(?:\.|,|and|or|$)/gi,
      /proficiency\s+(?:in|with)\s+([a-z\s]+?)(?:\.|,|and|or|$)/gi
    ];

    for (const pattern of patterns) {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        const capability = match[1].trim();
        const canonical = findCanonicalCapability(capability);
        if (canonical) {
          capabilities.push(canonical.canonicalId);
        } else {
          capabilities.push(capability.replace(/\s+/g, '_'));
        }
      }
    }

    return [...new Set(capabilities)];
  }

  /**
   * Score evidence against requirement keywords and capabilities
   */
  private scoreEvidenceAgainstRequirement(
    evidence: ScoredEvidenceItem,
    keywords: string[],
    requiredCapabilities: string[]
  ): { score: number; matchType: MatchType; matchedOn: string[] } {
    let directScore = 0;
    let inferredScore = 0;
    const matchedOn: string[] = [];
    const matchedCapabilities: string[] = [];

    // Check keywords against evidence keywords and content
    const evidenceText = `${evidence.contentSummary || ''} ${evidence.fullContent || ''}`.toLowerCase();
    const evidenceKeywords = new Set((evidence.keywords || []).map(k => k.toLowerCase()));

    for (const keyword of keywords) {
      const kw = keyword.toLowerCase();
      if (evidenceKeywords.has(kw) || evidenceText.includes(kw)) {
        directScore += 0.1;
        matchedOn.push(keyword);
      }
    }

    // Check required capabilities against evidence capabilities
    for (const requiredCap of requiredCapabilities) {
      // Check direct capabilities
      if ((evidence.allCapabilities || []).some(cap =>
        areCapabilitiesEquivalent(cap, requiredCap)
      )) {
        // Check if it's inferred or direct
        const isInferred = (evidence.inferredCapabilities || []).some(inf =>
          areCapabilitiesEquivalent(inf.capabilityId, requiredCap)
        );

        if (isInferred) {
          inferredScore += 0.2;
          matchedCapabilities.push(`${requiredCap} (inferred)`);
        } else {
          directScore += 0.25;
          matchedCapabilities.push(requiredCap);
        }
        matchedOn.push(requiredCap);
      }
    }

    // Add relevance score as a factor
    const relevanceBonus = evidence.relevance.overallScore * 0.3;

    // Calculate total score
    const totalDirect = directScore + relevanceBonus;
    const totalInferred = inferredScore;
    const totalScore = Math.min(1, totalDirect + totalInferred * 0.8); // Inferred counts slightly less

    // Determine match type
    const matchType: MatchType = totalDirect >= totalInferred ? 'DIRECT' : 'INFERRED';

    return {
      score: totalScore,
      matchType,
      matchedOn: [...new Set(matchedOn)]
    };
  }

  /**
   * Determine match status based on matched evidence
   */
  private determineMatchStatus(matchedEvidence: EvidenceMatch[]): MatchStatus {
    if (matchedEvidence.length === 0) return 'NONE';

    const topScore = matchedEvidence[0]?.relevanceScore || 0;

    if (topScore >= this.config.fullMatchThreshold) return 'FULL';
    if (topScore >= this.config.partialMatchThreshold) return 'PARTIAL';

    // Also check if we have multiple weaker matches that together provide coverage
    const combinedScore = matchedEvidence.reduce((sum, m) => sum + m.relevanceScore, 0);
    if (combinedScore >= this.config.fullMatchThreshold * 1.5) return 'FULL';
    if (combinedScore >= this.config.partialMatchThreshold * 1.5) return 'PARTIAL';

    return 'NONE';
  }

  /**
   * Identify gaps between requirement and matched evidence
   */
  private identifyGaps(
    requirement: RFPRequirement,
    matchedEvidence: EvidenceMatch[],
    requiredCapabilities: string[]
  ): RequirementGap[] {
    const gaps: RequirementGap[] = [];

    if (matchedEvidence.length === 0) {
      gaps.push({
        description: `No evidence found for: ${requirement.text.substring(0, 100)}...`,
        severity: requirement.mandatory ? 'CRITICAL' : 'MODERATE',
        impact: requirement.mandatory
          ? 'This requirement is mandatory. Missing evidence may result in disqualification.'
          : 'This requirement affects evaluation score but is not mandatory.'
      });
      return gaps;
    }

    // Check if all required capabilities are covered
    const coveredCapabilities = new Set<string>();
    for (const evidence of matchedEvidence) {
      for (const cap of evidence.content.split(/\s+/)) {
        coveredCapabilities.add(cap.toLowerCase());
      }
    }

    // Find uncovered capabilities
    for (const cap of requiredCapabilities) {
      const covered = [...coveredCapabilities].some(c =>
        areCapabilitiesEquivalent(c, cap)
      );

      if (!covered) {
        const capName = findCanonicalCapability(cap)?.canonicalName ||
          cap.replace(/_/g, ' ');
        gaps.push({
          description: `Limited evidence for: ${capName}`,
          severity: 'MODERATE',
          impact: `Stronger evidence for ${capName} would improve evaluation score.`
        });
      }
    }

    // Check if matches are only inferred
    const allInferred = matchedEvidence.every(m => m.matchType === 'INFERRED');
    if (allInferred && matchedEvidence.length > 0) {
      gaps.push({
        description: 'Evidence is based on inferred capabilities only',
        severity: 'MINOR',
        impact: 'Direct evidence would strengthen the proposal.'
      });
    }

    return gaps;
  }

  /**
   * Generate recommendations to address gaps
   */
  private generateRecommendations(
    requirement: RFPRequirement,
    gaps: RequirementGap[],
    requiredCapabilities: string[]
  ): GapRecommendation[] {
    const recommendations: GapRecommendation[] = [];

    for (const gap of gaps) {
      let recommendation: GapRecommendation;

      if (gap.severity === 'CRITICAL') {
        recommendation = {
          action: `Add evidence addressing: ${requirement.text.substring(0, 80)}...`,
          profileSection: this.suggestProfileSection(requirement.category),
          priority: 'HIGH',
          effort: 'MEDIUM',
          impact: 'Critical for proposal compliance'
        };
      } else if (gap.description.includes('Limited evidence')) {
        const capMatch = gap.description.match(/Limited evidence for: (.+)/);
        const capability = capMatch?.[1] || 'this capability';
        recommendation = {
          action: `Add past performance or documentation demonstrating ${capability}`,
          profileSection: 'Past Performance',
          priority: 'MEDIUM',
          effort: 'MEDIUM',
          impact: 'Would strengthen technical evaluation'
        };
      } else {
        recommendation = {
          action: `Add direct evidence for ${requirement.category} requirements`,
          profileSection: this.suggestProfileSection(requirement.category),
          priority: 'LOW',
          effort: 'LOW',
          impact: 'Would provide additional support'
        };
      }

      recommendations.push(recommendation);
    }

    return recommendations;
  }

  /**
   * Suggest profile section based on requirement category
   */
  private suggestProfileSection(category: string): string {
    const sectionMap: Record<string, string> = {
      technical: 'Capabilities',
      management: 'Key Personnel',
      past_performance: 'Past Performance',
      personnel: 'Key Personnel',
      pricing: 'Pricing Data',
      compliance: 'Certifications',
      other: 'Company Profile'
    };
    return sectionMap[category] || 'Company Profile';
  }

  /**
   * Extract relevant content excerpt from evidence
   */
  private extractRelevantContent(evidence: ScoredEvidenceItem, matchedOn: string[]): string {
    if (matchedOn.length === 0) {
      return evidence.contentSummary;
    }

    // Try to find a relevant excerpt
    const content = evidence.fullContent.toLowerCase();
    for (const term of matchedOn) {
      const idx = content.indexOf(term.toLowerCase());
      if (idx >= 0) {
        const start = Math.max(0, idx - 50);
        const end = Math.min(content.length, idx + term.length + 100);
        const excerpt = evidence.fullContent.substring(start, end);
        return `...${excerpt.trim()}...`;
      }
    }

    return evidence.contentSummary;
  }

  /**
   * Get inference chain for inferred matches
   */
  private getInferenceChain(evidence: ScoredEvidenceItem, matchedOn: string[]): string {
    for (const cap of matchedOn) {
      const inference = evidence.inferredCapabilities.find(inf =>
        areCapabilitiesEquivalent(inf.capabilityId, cap)
      );
      if (inference) {
        return inference.inferenceChain;
      }
    }
    return 'Capability inferred from evidence';
  }

  /**
   * Format citation for evidence
   */
  private formatCitation(evidence: ScoredEvidenceItem, matchType: MatchType): string {
    if (matchType === 'INFERRED') {
      return `[${evidence.evidenceId}, inferred]`;
    }
    return `[${evidence.evidenceId}]`;
  }

  /**
   * Calculate confidence score for the match
   */
  private calculateConfidence(matchedEvidence: EvidenceMatch[]): number {
    if (matchedEvidence.length === 0) return 0;

    // Average of top 3 matches, weighted by direct vs inferred
    const topMatches = matchedEvidence.slice(0, 3);
    let weightedSum = 0;
    let weightTotal = 0;

    for (const match of topMatches) {
      const weight = match.matchType === 'DIRECT' ? 1.0 : 0.8;
      weightedSum += match.relevanceScore * weight;
      weightTotal += weight;
    }

    return weightTotal > 0 ? weightedSum / weightTotal : 0;
  }

  /**
   * Deduplicate and prioritize recommendations
   */
  private deduplicateRecommendations(recommendations: GapRecommendation[]): GapRecommendation[] {
    const seen = new Set<string>();
    const unique: GapRecommendation[] = [];

    // Sort by priority first
    const sorted = [...recommendations].sort((a, b) => {
      const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    for (const rec of sorted) {
      const key = `${rec.profileSection}:${rec.action.substring(0, 50)}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(rec);
      }
    }

    return unique;
  }
}

// Export singleton instance
export const requirementsMatcher = new RequirementsMatcher();

// Export convenience function
export function matchRequirements(
  requirements: RFPRequirement[],
  library: ContractScoredLibrary
): MatchingResult {
  return requirementsMatcher.matchRequirements(requirements, library);
}
